import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palmtree, Check, X, Ban } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function HolidaysPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [params, setParams] = useSearchParams();
  const [pendingFilter, setPendingFilter] = useState(() => {
    const s = params.get("status");
    return ["pending", "approved", "rejected", "cancelled", "all"].includes(s) ? s : "pending";
  });
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (pendingFilter === "pending") next.delete("status"); else next.set("status", pendingFilter);
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [pendingFilter]);
  const [openAdj, setOpenAdj] = useState(false);
  const [adj, setAdj] = useState({ employee_id: "", year: new Date().getFullYear(), adjustment_days: 0, notes: "" });
  const [openLog, setOpenLog] = useState(false);
  const [logForm, setLogForm] = useState({ employee_id: "", date: new Date().toISOString().slice(0,10), days: 1, notes: "" });
  const [openEnt, setOpenEnt] = useState(false);
  const [ent, setEnt] = useState({ employee_id: "", year: new Date().getFullYear(), allowed_days: 20, adjustment_days: 0, notes: "" });

  const load = async () => {
    try {
      const [ent, ents] = await Promise.all([
        api.get("/holidays/entitlements", { params: { year } }),
        api.get("/holidays/entries", { params: { year } }),
      ]);
      setRows(ent.data);
      setRequests(ents.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decide = async (id, decision, note) => {
    try {
      await api.patch(`/holidays/entries/${id}/decision`, { decision, note });
      toast.success(`Request ${decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "cancelled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredReqs = requests.filter((r) => {
    const st = r.status || "approved";
    return pendingFilter === "all" ? true : st === pendingFilter;
  });
  const pendingCount = requests.filter((r) => r.status === "pending").length;

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const submitAdj = async () => {
    try {
      await api.post("/holidays/adjust", { ...adj, year: Number(adj.year), adjustment_days: Number(adj.adjustment_days) });
      toast.success("Adjustment saved");
      setOpenAdj(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitLog = async () => {
    try {
      await api.post("/holidays/entries", { ...logForm, days: Number(logForm.days) });
      toast.success("Holiday recorded");
      setOpenLog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitEnt = async () => {
    try {
      await api.post("/holidays/entitlements", { ...ent, year: Number(ent.year), allowed_days: Number(ent.allowed_days), adjustment_days: Number(ent.adjustment_days) });
      toast.success("Entitlement set");
      setOpenEnt(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Annual leave</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Holidays</h1>
          <p className="text-muted-foreground mt-1">Entitlement, accruals, and holidays taken — per employee per year.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label>Year</Label>
            <Input type="number" className="w-28" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="hol-year" />
          </div>

          <Dialog open={openLog} onOpenChange={setOpenLog}>
            <DialogTrigger asChild><Button variant="outline" data-testid="hol-log-open"><Palmtree className="w-4 h-4 mr-1" /> Log holiday</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log a holiday entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee ID</Label>
                  <Select value={logForm.employee_id} onValueChange={(v) => setLogForm({ ...logForm, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{rows.map((r) => <SelectItem key={r.employee_id} value={r.employee_id}>{r.employee?.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} /></div>
                <div><Label>Days (0.5 half-day, 1 full day)</Label><Input type="number" step="0.5" value={logForm.days} onChange={(e) => setLogForm({ ...logForm, days: e.target.value })} /></div>
                <div><Label>Notes</Label><Input value={logForm.notes} onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenLog(false)}>Cancel</Button>
                <Button onClick={submitLog} data-testid="hol-log-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openEnt} onOpenChange={setOpenEnt}>
            <DialogTrigger asChild><Button variant="outline" data-testid="hol-ent-open">Set entitlement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Set annual entitlement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Employee</Label>
                  <Select value={ent.employee_id} onValueChange={(v) => setEnt({ ...ent, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{rows.map((r) => <SelectItem key={r.employee_id} value={r.employee_id}>{r.employee?.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Year</Label><Input type="number" value={ent.year} onChange={(e) => setEnt({ ...ent, year: e.target.value })} /></div>
                <div><Label>Allowed days</Label><Input type="number" step="0.5" value={ent.allowed_days} onChange={(e) => setEnt({ ...ent, allowed_days: e.target.value })} /></div>
                <div><Label>Adjustment days</Label><Input type="number" step="0.5" value={ent.adjustment_days} onChange={(e) => setEnt({ ...ent, adjustment_days: e.target.value })} /></div>
                <div><Label>Notes</Label><Input value={ent.notes} onChange={(e) => setEnt({ ...ent, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenEnt(false)}>Cancel</Button>
                <Button onClick={submitEnt} data-testid="hol-ent-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-border shadow-none" data-testid="holiday-requests">
        <div className="p-5 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="tiny-label">Holiday requests</div>
            <div className="font-display text-lg font-semibold">
              {pendingCount > 0 ? <>{pendingCount} awaiting your approval</> : "All requests handled"}
            </div>
          </div>
          <Select value={pendingFilter} onValueChange={setPendingFilter}>
            <SelectTrigger className="w-40" data-testid="hol-req-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending ({pendingCount})</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Days</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredReqs.slice(0, 50).map((r) => {
                const st = r.status || "approved";
                return (
                  <tr key={r.id} className="border-t border-border" data-testid={`hol-req-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{r.employee_name || r.employee_id}</td>
                    <td className="px-4 py-3 text-xs font-mono">{r.date}</td>
                    <td className="px-4 py-3 text-right font-mono">{Number(r.days).toFixed(1)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[240px] truncate">{r.notes || "—"}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={
                      st === "pending" ? "text-amber-700 border-amber-200 bg-amber-50" :
                      st === "approved" ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                      st === "rejected" ? "text-red-700 border-red-200 bg-red-50" :
                      "text-muted-foreground"}>{st}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {st === "pending" ? (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => decide(r.id, "approve")} data-testid={`hol-approve-${r.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                          <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => { const n = window.prompt("Reason (optional):"); decide(r.id, "reject", n); }} data-testid={`hol-reject-${r.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => decide(r.id, "cancel")}><Ban className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                  </tr>
                );
              })}
              {filteredReqs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No requests match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3 text-right">Allowed</th>
                <th className="px-4 py-3 text-right">Adjusted</th>
                <th className="px-4 py-3 text-right">Taken</th>
                <th className="px-4 py-3 text-right">Remaining</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-border" data-testid={`hol-row-${r.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.employee?.full_name}</div>
                    <div className="text-xs text-muted-foreground">{r.employee?.department || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.allowed_days).toFixed(1)}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.adjustment_days || 0).toFixed(1)}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(r.taken_days).toFixed(1)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{Number(r.remaining_days).toFixed(1)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => { setAdj({ employee_id: r.employee_id, year, adjustment_days: 0, notes: "" }); setOpenAdj(true); }} data-testid={`hol-adjust-${r.employee_id}`}>Adjust</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No employees found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={openAdj} onOpenChange={setOpenAdj}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adjust entitlement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Adjustment (days, can be negative)</Label><Input type="number" step="0.5" value={adj.adjustment_days} onChange={(e) => setAdj({ ...adj, adjustment_days: e.target.value })} data-testid="hol-adj-days" /></div>
            <div><Label>Notes</Label><Input value={adj.notes} onChange={(e) => setAdj({ ...adj, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdj(false)}>Cancel</Button>
            <Button onClick={submitAdj} data-testid="hol-adj-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
